import { Dayjs } from 'dayjs'
import { getAdminErrorMessage } from '../adminErrorModel'
import { JobsToInvoiceComparison, JobsToInvoiceCompareRequest } from './jobsToInvoiceCompareApi'

export type CompareDateRange = [Dayjs | null, Dayjs | null] | null

export type CompareFormState = {
  tenantId: number | undefined
  dateRange: CompareDateRange
  divisionGroups: string[]
  workAreaStatuses: number[]
}

export type DifferenceRow = {
  key: string
  jobId: number
  field: string
  current: string
  facts: string
}

export const defaultJobsToInvoiceComparison = (): JobsToInvoiceComparison => ({
  isIdentical: true,
  currentJobCount: 0,
  factsJobCount: 0,
  missingFromFacts: [],
  extraInFacts: [],
  differences: [],
})

const toApiDate = (value: Dayjs): string => value.format('YYYY-MM-DD')

const toListFilter = <T>(values: T[]): T[] | null => {
  return values.length === 0 ? null : values
}

export const toCompareRequest = (state: CompareFormState): JobsToInvoiceCompareRequest | null => {
  if (state.tenantId === undefined) {
    return null
  }
  const [start, end] = state.dateRange ?? [null, null]
  const hasRange = start !== null && end !== null
  return {
    tenantId: state.tenantId,
    filters: {
      startDate: hasRange ? toApiDate(start) : null,
      endDate: hasRange ? toApiDate(end) : null,
      divisionGroups: toListFilter(state.divisionGroups.map(group => group.trim()).filter(group => group !== '')),
      workAreaStatuses: toListFilter(state.workAreaStatuses),
    },
  }
}

export const toDifferenceRows = (comparison: JobsToInvoiceComparison): DifferenceRow[] => {
  return comparison.differences.flatMap(job =>
    job.differences.map(difference => ({
      key: `${job.jobId}-${difference.field}`,
      jobId: job.jobId,
      field: difference.field,
      current: difference.current,
      facts: difference.facts,
    }))
  )
}

export const getComparisonSummary = (comparison: JobsToInvoiceComparison): string => {
  if (comparison.isIdentical) {
    return 'The facts match the current discovery.'
  }
  const differingJobs = comparison.differences.length
  return [
    `${comparison.missingFromFacts.length} missing from facts`,
    `${comparison.extraInFacts.length} extra in facts`,
    `${differingJobs} ${differingJobs === 1 ? 'job' : 'jobs'} with field differences`,
  ].join(', ') + '.'
}

export const getCompareErrorMessage = (error: unknown): string => {
  return getAdminErrorMessage(error, 'Failed to run the comparison. Please try again.')
}
